// 1 vs 1 — porównanie skuteczności dwóch projektantów z WYŁĄCZENIEM kosztów
// podwykonawczych (marża własnej pracy: wpływy − pensja − narzut).

import { useState } from "react";

import { PageLoader } from "@/components/ui/Spinner";
import { SectionCard } from "@/features/power_projects/components/ui";

import { useHeadToHead } from "./hooks";
import type { DesignerOverviewRow, HeadToHeadSide, Variant } from "./types";
import { fmtPct, fmtPln, HELP, InfoDot } from "./ui";

type MetricDef = {
  key: keyof HeadToHeadSide;
  label: string;
  help?: string;
  fmt: (v: unknown) => string;
  /** true → wyższa wartość wygrywa; false → niższa; undefined → bez werdyktu */
  higherWins?: boolean;
};

const num = (v: unknown): number | null => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const METRICS: MetricDef[] = [
  { key: "projects_count", label: "Aktywne projekty", fmt: (v) => String(v ?? "—") },
  { key: "inflow", label: "Wpływy (prognoza)", help: HELP.inflow, fmt: (v) => fmtPln(v as string) },
  { key: "salary_to_completion", label: "Pensja do końca", help: HELP.salary, fmt: (v) => fmtPln(v as string), higherWins: false },
  { key: "overhead", label: "Narzut", help: HELP.overhead, fmt: (v) => fmtPln(v as string), higherWins: false },
  { key: "own_margin_ex_sub", label: "Marża własnej pracy (bez podwyk.)", help: HELP.h2h, fmt: (v) => fmtPln(v as string), higherWins: true },
  { key: "own_margin_ex_sub_pct", label: "Marża własna %", fmt: (v) => fmtPct(v as string), higherWins: true },
  { key: "own_margin_per_month", label: "Marża własna / mies.", help: HELP.marginPerMonth, fmt: (v) => fmtPln(v as string), higherWins: true },
  { key: "subcontractor_excluded", label: "Podwykonawcy (wyłączone z oceny)", help: HELP.subcontractor, fmt: (v) => fmtPln(v as string) },
  { key: "full_margin", label: "Marża pełna (informacyjnie)", help: HELP.margin, fmt: (v) => fmtPln(v as string) },
  { key: "avg_milestones_pct", label: "Postęp śr. (kamienie)", help: HELP.progress, fmt: (v) => (v != null ? `${v}%` : "—"), higherWins: true },
  { key: "on_track_pct", label: "Projekty na czas (%)", fmt: (v) => (v != null ? `${v}%` : "—"), higherWins: true },
  { key: "forecast_slip_days_median", label: "Poślizg (mediana dni)", help: HELP.slip, fmt: (v) => (v != null ? `${v} dni` : "—"), higherWins: false },
  { key: "done_per_available_day", label: "Kroki / dzień dostępny", help: HELP.pace, fmt: (v) => (v != null ? String(v) : "—"), higherWins: true },
  { key: "returns_median_days", label: "Odbicie piłeczki (mediana dni)", help: HELP.returns, fmt: (v) => (v != null ? `${v} dni` : "—"), higherWins: false },
  { key: "sla_breaches", label: "Naruszenia SLA", fmt: (v) => String(v ?? 0), higherWins: false },
  { key: "revenue_per_available_day", label: "Przychód / dzień dostępny", fmt: (v) => (v != null ? fmtPln(v as string) : "—"), higherWins: true },
];

function winner(m: MetricDef, a: HeadToHeadSide, b: HeadToHeadSide): "a" | "b" | null {
  if (m.higherWins === undefined) return null;
  const va = num(a[m.key]);
  const vb = num(b[m.key]);
  if (va == null || vb == null || va === vb) return null;
  return (va > vb) === m.higherWins ? "a" : "b";
}

const WIN_CLS = "font-bold text-emerald-700 dark:text-emerald-400";

export function HeadToHeadSection({
  designers,
  variant,
}: {
  designers: DesignerOverviewRow[];
  variant: Variant;
}) {
  const [a, setA] = useState<string | null>(designers[0]?.designer_id ?? null);
  const [b, setB] = useState<string | null>(designers[1]?.designer_id ?? null);
  const { data, isLoading } = useHeadToHead(a, b, variant);

  const selectCls =
    "border border-[var(--color-border)] rounded px-2 py-1.5 text-sm bg-transparent min-w-48";

  return (
    <SectionCard
      title={<>1 vs 1 — skuteczność własnej pracy (bez kosztów podwykonawczych)<InfoDot text={HELP.h2h} /></>}
    >
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={a ?? ""} onChange={(e) => setA(e.target.value || null)} className={selectCls}>
          <option value="">— wybierz koordynatora A —</option>
          {designers.map((d) => (
            <option key={d.designer_id} value={d.designer_id}>{d.name}</option>
          ))}
        </select>
        <span className="text-sm font-bold text-gray-400">VS</span>
        <select value={b ?? ""} onChange={(e) => setB(e.target.value || null)} className={selectCls}>
          <option value="">— wybierz koordynatora B —</option>
          {designers.map((d) => (
            <option key={d.designer_id} value={d.designer_id}>{d.name}</option>
          ))}
        </select>
      </div>

      {designers.length < 2 && (
        <p className="text-sm text-gray-400">Potrzeba co najmniej dwóch koordynatorów ZE.</p>
      )}
      {a && b && a === b && (
        <p className="text-sm text-amber-600">Wybierz dwóch różnych koordynatorów.</p>
      )}
      {a && b && a !== b && (isLoading || !data ? (
        <PageLoader />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-[var(--color-border)]">
                <th className="py-2 pr-3">Metryka</th>
                <th className="py-2 pr-3 text-right">{data.a.name}</th>
                <th className="py-2 text-right">{data.b.name}</th>
              </tr>
            </thead>
            <tbody>
              {METRICS.map((m) => {
                const w = winner(m, data.a, data.b);
                return (
                  <tr key={m.key} className="border-b border-[var(--color-border)]">
                    <td className="py-1.5 pr-3 text-gray-600 dark:text-gray-300">
                      {m.label}
                      {m.help && <InfoDot text={m.help} />}
                    </td>
                    <td className={`py-1.5 pr-3 text-right tabular-nums ${w === "a" ? WIN_CLS : ""}`}>
                      {m.fmt(data.a[m.key])}{w === "a" ? " ✓" : ""}
                    </td>
                    <td className={`py-1.5 text-right tabular-nums ${w === "b" ? WIN_CLS : ""}`}>
                      {m.fmt(data.b[m.key])}{w === "b" ? " ✓" : ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-xs text-gray-400 mt-2">
            Okno tempa: {data.window.from} — {data.window.to} · wariant prognozy: {data.variant} ·
            ✓ = lepszy wynik w metryce z werdyktem
          </p>
        </div>
      ))}
    </SectionCard>
  );
}
